import { isEmpty } from 'lodash';

import { QuoteType, RegUser, RegUserMigration } from '@common/types';
import { NON_CHRONIC } from '@healthquote/constants';
import { SessionStorage } from '@services/storage/storage';
import {
  HealthAddOns,
  HealthProductBoundary,
  HealthProductType,
  HealthQuotePlanDetails,
  SumInsuredValue
} from '@store/healthquote';
import { dayjs } from '@ui';
import {
  ABCD,
  CHRONIC_CARE,
  DATE_FORMAT,
  HEALTH_INSURANCE_PRODUCTS,
  OPD
} from '@utils/constants';

import {
  FinalizeMigrationHealthQuoteRequest,
  HealthCoverSuggestionRequest,
  HealthEstimateRequest,
  HealthPremiumRequest,
  HealthQuoteFinalizeRequest,
  OptimaSecureMigrationPremiumRequest
} from './types';

export const healthSuggestionTransform = (
  { members, pincode, ped, product }: HealthQuotePlanDetails,
  quote?: string
): HealthCoverSuggestionRequest => {
  return {
    members,
    pincode: pincode!.toString(),
    ped: ped.join(','),
    products: product && quote === QuoteType.Fast ? [product] : undefined
  };
};

export const healthEstimateTransform = (
  plan: HealthQuotePlanDetails,
  quote?: string
): HealthEstimateRequest => {
  return {
    ...healthSuggestionTransform(plan, quote),
    sum_insured: plan.sumInsured
  };
};

export const healthPremiumTransform = (
  {
    members,
    pincode,
    ped,
    product,
    sumInsured,
    planType,
    deductible,
    porting,
    policy_start_date,
    loyalty_discount,
    existing_policy_number,
    optedEmi,
    tenure
  }: HealthQuotePlanDetails,
  addOns: HealthAddOns,
  emiEnabled?: boolean,
  otherCovers?: SumInsuredValue[],
  boundaries?: HealthProductBoundary
): HealthPremiumRequest => {
  // Only add med_conditions if boundaries?.conditionRequired is true
  return {
    members: members.map((member) => ({
      ...member,
      ...(boundaries?.conditionRequired
        ? {
            med_conditions: member.med_conditions?.length
              ? member.med_conditions
              : [NON_CHRONIC]
          }
        : {})
    })),
    pincode: pincode!.toString(),
    ped: ped.join(','),
    product,
    sum_insured:
      boundaries?.type !== HealthProductType.SuperTopUp
        ? [
            sumInsured === 'unlimited' ? 'unlimited' : Number(sumInsured),
            ...(otherCovers || []).map((cover) =>
              cover === 'unlimited' ? 'unlimited' : Number(cover)
            )
          ]
        : [sumInsured === 'unlimited' ? 'unlimited' : Number(sumInsured)],
    deductible,
    plan_type: planType,
    porting,
    ...(porting && policy_start_date
      ? { policy_start_date: dayjs(policy_start_date).format(DATE_FORMAT) }
      : {}),
    loyalty_discount,
    ...(loyalty_discount && { existing_policy_number }),
    ...(emiEnabled ? { emi_opted: optedEmi } : {}),
    ...(tenure ? { tenure } : {}),
    addons: addOns.length
      ? addOns.map((addOn) => ({
          key: addOn.key,
          included: addOn.included ?? false,
          sub_options: addOn.subOptionValues
            ? Object.keys(addOn.subOptionValues).map((key) => ({
                key: key,
                value: addOn.subOptionValues
                  ? addOn.subOptionValues[key].value
                  : undefined
              }))
            : []
        }))
      : []
  };
};

export const finalizeHealthQuoteTransform = (
  {
    sumInsured,
    members,
    pincode,
    tenure,
    planType,
    deductible,
    product,
    porting,
    policy_start_date,
    loyalty_discount,
    existing_policy_number,
    optedEmi,
    emiFrequency
  }: HealthQuotePlanDetails,
  addOns: HealthAddOns,
  user: RegUser,
  boundaries?: HealthProductBoundary
): HealthQuoteFinalizeRequest => {
  const team = SessionStorage?.get('team');
  const membersWithAddOns = members.map((member) => {
    // Create a new member object instead of mutating the existing one
    const updatedMember = { ...member };

    member.addons?.forEach((addOn) => {
      // if OPD or CHRONIC_CARE is included map sub options to med_conditions required for active one
      if (addOn.key === OPD && addOn.included) {
        updatedMember.opd_care = true;
        updatedMember.med_conditions = addOn.sub_options?.map(
          (subOpt) => subOpt.value
        );
      }
      if (addOn.key === CHRONIC_CARE && addOn.included) {
        updatedMember.cc = true;
        updatedMember.med_conditions = addOn.sub_options?.map(
          (subOpt) => subOpt.value
        );
      }
      if (addOn.key === ABCD && addOn.included) {
        updatedMember.abcd = true;
      }
    });

    // Ensure med_conditions defaults to [NON_CHRONIC] if empty
    if (!updatedMember.med_conditions?.length && boundaries?.conditionRequired) {
      updatedMember.med_conditions = [NON_CHRONIC];
    }

    return updatedMember;
  });

  // clean up med_conditions if opd_care or cc is not included for active one
  const filteredMembers = membersWithAddOns.map((member) => {
    const medConditions = member.opd_care || member.cc ? member.med_conditions : [];

    return {
      ...member,
      id: undefined,
      diseases: undefined,
      med_conditions: medConditions
    };
  });

  //For Activ One, we need to send the filtered members because it has medical conditions as rider options whereas in Activ Fit there are no medical conditions.
  const finalMembers =
    product === HEALTH_INSURANCE_PRODUCTS.ACTIV_ONE
      ? filteredMembers
      : membersWithAddOns;

  return {
    product_details: {
      sum_insured: sumInsured,
      members: finalMembers,
      pincode: pincode!.toString(),
      tenure: tenure!,
      plan_type: planType,
      deductible,
      porting,
      ...(porting && policy_start_date
        ? { policy_start_date: dayjs(policy_start_date).format(DATE_FORMAT) }
        : {}),
      loyalty_discount,
      ...(loyalty_discount && { existing_policy_number }),
      ...(optedEmi
        ? {
            emi_opted: optedEmi,
            emi_frequency: emiFrequency
          }
        : {}),
      ...(addOns.length
        ? addOns.reduce((prev, addOn) => {
            if (!addOn.noDefaultOption) {
              //For all the existing addons
              const subOptions = addOn.subOptionValues
                ? Object.keys(addOn.subOptionValues).reduce(
                    (prevSubOptVal, subOptKey) => {
                      return {
                        ...prevSubOptVal,
                        [subOptKey]: addOn.subOptionValues?.[subOptKey].value
                      };
                    },
                    {}
                  )
                : {};
              return { ...prev, [addOn.key]: addOn.included, ...subOptions };
            } else {
              //For the Addons with sub add ons
              return {
                ...prev,
                [addOn.key]: addOn?.subAddOns ? true : false,
                [addOn?.subOptions[0]?.key]: addOn?.subAddOns
              };
            }
          }, {})
        : {})
    },
    ...(!isEmpty(team) && {
      other_details: {
        team: team!
      }
    }),
    product: product,
    user: {
      phone_num: user.mobile,
      country_code: user.country_code,
      otp: user.otp,
      // Full name will be there when finalizing the quote
      full_name: user.fullName!
    }
  };
};

export const healthBoundariesTransform = (
  {
    pincode,
    ped,
    product,
    sumInsured,
    planType,
    deductible,
    porting,
    tenure,
    members,
    loyalty_discount,
    existing_policy_number
  }: HealthQuotePlanDetails,
  products?: string[]
) => {
  const currMembers = members.filter((item) => item.age);
  return {
    members: currMembers,
    products: products ? products : [product],
    sum_insured:
      sumInsured === 'unlimited' ? ('unlimited' as const) : Number(sumInsured),
    pincode: pincode?.toString(),
    plan_type: planType !== '' ? planType : undefined,
    ped: ped.join(','),
    tenure,
    porting,
    deductible,
    loyalty_discount,
    ...(loyalty_discount && { existing_policy_number })
  };
};

//OS Migration
export const healthPremiumMigrationTransform = (
  policyNumber: string,
  sumInsured: number,
  addOns: HealthAddOns,
  oldProduct: string,
  newProduct: string,
  planType: string
): OptimaSecureMigrationPremiumRequest => {
  return {
    old_product: oldProduct,
    product: newProduct,
    policy_number: policyNumber,
    sum_insured: sumInsured,
    plan_type: planType,
    addons: addOns.length
      ? addOns.map((addOn) => ({
          key: addOn.key,
          included: addOn.included ?? false,
          sub_options: addOn.subOptionValues
            ? Object.keys(addOn.subOptionValues).map((key) => ({
                key: key,
                value: addOn.subOptionValues
                  ? addOn.subOptionValues[key].value
                  : undefined
              }))
            : []
        }))
      : []
  };
};

export const finalizeMigrationHealthQuoteTransform = (
  { sumInsured, tenure, product, planType }: HealthQuotePlanDetails,
  addOns: HealthAddOns,
  user: RegUserMigration,
  { old_product, old_sum_insured, old_tenure, policy_number }: any
): FinalizeMigrationHealthQuoteRequest => {
  return {
    product: product,
    product_details: {
      plan_type: planType || '',
      old_product,
      old_sum_insured,
      old_tenure,
      policy_number,
      sum_insured: sumInsured,
      tenure: tenure!,
      ...(addOns.length
        ? addOns.reduce((prev, addOn) => {
            const subOptions = addOn.subOptionValues
              ? Object.keys(addOn.subOptionValues).reduce(
                  (prevSubOptVal, subOptKey) => {
                    return {
                      ...prevSubOptVal,
                      [subOptKey]: addOn.subOptionValues?.[subOptKey].value
                    };
                  },
                  {}
                )
              : {};
            return { ...prev, [addOn.key]: addOn.included, ...subOptions };
          }, {})
        : {})
    },
    user: {
      phone_num: user.mobile ?? '',
      otp: user.otp,
      email: user.email,
      // Full name will be there when finalizing the quote
      full_name: user.fullName!,
      country_code: user.country_code ?? ''
    }
  };
};
